//api.js

// Bestimmt die Basis-URL für die API-Anfragen dynamisch.
// Wenn die Anwendung auf der Produktionsdomain läuft, wird die Produktions-API verwendet.
// Ansonsten wird die lokale Entwicklungs-API auf Port 8000 angesprochen.
export const API_BASE_URL = window.location.hostname.includes("bonnerwettwetter.de") ? "https://bonnerwettwetter.de/api" : `${window.location.protocol}//${window.location.hostname}:8000`;

/**
 * Eine zentrale Helferfunktion für alle API-Anfragen.
 * Sie fügt automatisch 'credentials: include' hinzu, um Cookies mitzusenden,
 * und verarbeitet die JSON-Antworten sowie Fehler einheitlich.
 * @param {string} endpoint Der API-Endpunkt (z.B. '/login').
 * @param {object} options Die Optionen für den fetch-Aufruf (z.B. Methode, Header, Body).
 * @returns {Promise<object>} Die JSON-Antwort vom Server.
 * @throws {Error} Wirft einen Fehler mit der Fehlermeldung vom Server, wenn die Anfrage fehlschlägt.
 */
export async function request(endpoint, options = {}) {
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    credentials: 'include',
    ...options,
    headers: {
      // Bei FormData setzt der Browser den Content-Type (inkl. Boundary) selbst.
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...options.headers,
    },
  });
  let data = null
  const contentType = response.headers.get('content-type') || ''
  // Antwort nur als JSON parsen, wenn der Header stimmt.
  if (contentType.includes('application/json')) data = await response.json()
  if (!response.ok || data?.success === false) {
    throw new Error(data?.error || 'Ein interessanter unbekannter Fehler ist aufgetreten: ' + JSON.stringify(data));
  }
  return data
}

/**
 * Lädt eine Datei über XMLHttpRequest hoch, um den Fortschritt verfolgen zu können.
 * @param {File} file Die hochzuladende Datei.
 * @param {object} options Optionen wie onProgress-Callback und isAvatar-Flag.
 * @returns {Promise<object>} Die Antwort vom Server.
 */
export function uploadFile(file, { onProgress, isAvatar = false } = {}) {
  return new Promise((resolve, reject) => {
    const formData = new FormData()
    formData.append('file', file)
    if (isAvatar) formData.append('type', 'avatar')
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${API_BASE_URL}/users/files/upload`) // Wichtig für Cookies
    xhr.withCredentials = true;
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && typeof onProgress === 'function') {
        const percent = Math.round((event.loaded / event.total) * 100)
        onProgress(percent, event.loaded, event.total)
      }
    }
    xhr.onload = () => {
      let data = null
      try {
        data = JSON.parse(xhr.responseText)
      } catch {
        data = null
      }
      if (xhr.status >= 200 && xhr.status < 300 && data?.success !== false) resolve(data)
      else reject(new Error(data?.error || `Upload fehlgeschlagen (${xhr.status})`))
    }
    xhr.onerror = () => reject(new Error('Upload fehlgeschlagen'))
    xhr.send(formData)
  })
}
/**
 * Lädt eine Datei vom Server herunter.
 * @param {string} fileRef Die Referenz zur Datei.
 * @returns {Promise<Response>} Die rohe fetch-Antwort.
 */
export async function downloadFile(fileRef) {
  const response = await fetch(`${API_BASE_URL}/users/files/${encodeURIComponent(fileRef)}`, {credentials: 'include'})
  if (!response.ok) throw new Error(`Download fehlgeschlagen (${response.status})`)
  return response;
}
/**
 * Lädt eine Datei vom Server als Blob herunter.
 * @param {string} fileRef Die Referenz zur Datei.
 * @returns {Promise<Blob>} Die Datei als Blob.
 */
export async function downloadFileBlob(fileRef) {
  const response = await fetch(`${API_BASE_URL}/users/files/${encodeURIComponent(fileRef)}`, {credentials: 'include'})
  if (!response.ok) {
    const text = await response.text()
    throw new Error(text || `Download fehlgeschlagen (${response.status})`)
  }
  return await response.blob()
}
/**
 * Löst einen Browser-Download für eine Datei vom Server aus.
 * @param {string} fileRef Die Referenz zur Datei.
 * @param {string} filename Der gewünschte Dateiname für den Download.
 */
export async function downloadFileAsLink(fileRef, filename = 'datei') {
  const response = await fetch(`${API_BASE_URL}/users/files/${encodeURIComponent(fileRef)}`, {credentials: 'include'})
  if (!response.ok) throw new Error(`Download fehlgeschlagen (${response.status})`)
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// --- Authentifizierung ---

/**
 * Sendet eine Login-Anfrage an das Backend.
 * @param {string} email
 * @param {string} password
 */
export async function login(email, password) {
  return request('/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })
}

/**
 * Sendet den Discord-Autorisierungscode an das Backend.
 * @param {string} code Der Code von Discord.
 * @param {string} url Die Redirect-URL.
 */
export async function discordLogin(code, url) {
  return request('/discord', {method: 'POST', body: JSON.stringify({ code, url })})
}

/**
 * Sendet eine Registrierungsanfrage an das Backend (für beide Schritte).
 * @param {string} email
 * @param {string} password
 * @param {string|null} code Der Bestätigungscode (nur für Schritt 2).
 */
export async function register(email, password, code = null) {
  const body = { email, password }
  if (code) body.code = code

  return request('/register', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function getMyInfo() {
  return request('/users/me')
}

export async function logout() {
  return request('/logout', {
    method: 'POST',
  })
}

export async function getAllBets() {
  return request('/wetten/all')
}

export async function getRunningBets() {
  return request('/wetten/running')
}

export async function getMyBets() {
  return request('/wetten/my')
}

export async function getBetById(wetteId) {
  return request(`/wetten/${wetteId}`)
}

export async function getOnlineUsers() {
  return request(`/users/online`)
}

export async function placeBet(wetteId, positionId, balance) {
  return request(`/wetten/${wetteId}/set`, {
    method: 'POST',
    body: JSON.stringify({ position: positionId, balance }),
  })
}

export async function balanceAufladen(balance) {
  return request(`/users/aufladen/${balance}`, {
    method: 'POST',
    body: JSON.stringify({ balance }),
  })
}


export async function getUserById(id) {
  return request(`/users/${id}`)
}

export async function getUserBets(id) {
  return request(`/users/${id}/bets`)
}

/**
 * Ruft öffentliche Informationen über einen Benutzer anhand seiner E-Mail ab.
 * @param {string} email Die E-Mail des Benutzers.
 */
export async function getUserByEmail(email) {
  return request(`/users/by-email/${encodeURIComponent(email)}`)
}

export async function getVapid() {
  return request(`/users/vapid`)
}


/**
 * Ändert das Passwort des aktuell eingeloggten Benutzers.
 * @param {string} currentPassword
 * @param {string} newPassword
 */
export async function changePassword(currentPassword, newPassword) {
  return request('/users/me/password', {
    method: 'PUT',
    body: JSON.stringify({ currentPassword, newPassword }),
  })
}

/**
 * Ändert die E-Mail des aktuell eingeloggten Benutzers.
 * @param {string} newEmail
 * @param {string} password
 */
export async function changeEmail(newEmail, password) {
  return request('/users/me/email', {
    method: 'PUT',
    body: JSON.stringify({ newEmail, password }),
  })
}

/**
 * Löscht das Konto des aktuell eingeloggten Benutzers.
 * @param {string} password
 */
export async function deleteAccount(password) {
  return request('/users/me', {
    method: 'DELETE',
    body: JSON.stringify({ password }),
  })
}

/**
 * Gibt die korrekte Discord-OAuth-URL basierend auf der aktuellen Host-Domain zurück.
 * @returns {string} Die Discord-Login-URL.
 */
export function getDiscordURL() {
  const loginWithDiscordLinks = {
    "other": "https://discord.com/oauth2/authorize?client_id=1508448201252798605&response_type=code&redirect_uri=http%3A%2F%2Flocalhost%3A8080%2Flogin&scope=identify+email",
    "bonnerwettwetter.de": "https://discord.com/oauth2/authorize?client_id=1508448201252798605&response_type=code&redirect_uri=https%3A%2F%2Fbonnerwettwetter.de%2Flogin&scope=identify+email",
    "nxlc.de": "https://discord.com/oauth2/authorize?client_id=1508448201252798605&response_type=code&redirect_uri=https%3A%2F%2Fnxlc.de%2Flogin&scope=identify+email"
  }
  const currentHostname = window.location.hostname;
  return loginWithDiscordLinks[currentHostname] || loginWithDiscordLinks.other;
}

/**
 * Normalisiert eine URL, um sie relativ zur API-Basis-URL aufzulösen.
 * Nützlich für das Anzeigen von Bildern, die vom Backend bereitgestellt werden.
 * @param {string} url Die aufzulösende URL.
 * @returns {string} Die vollständige, absolute URL.
 */
export function resolveApiUrl(url) {
  if (!url || typeof url !== "string") return url;
  // Absolute URLs bleiben unverändert.
  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(url)) return url; //es ist absolute url
  if (url.startsWith("/")) {
    // Behandelt den Fall, dass sowohl Basis-URL als auch Pfad mit /api beginnen.
    const baseEndsWithApi = /\/api\/?$/.test(API_BASE_URL);
    const pathStartsWithApi = /^\/api(\/|$)/.test(url);
    const path = (baseEndsWithApi && pathStartsWithApi) ? url.replace(/^\/api/, "") : url;
    return `${API_BASE_URL}${path}`;
  }
  console.warn("[resolveApiUrl] Unbekannte URL-Form, wird unverändert zurückgegeben:", url);
  return url;
}

/**
 * Löst einen Browser-Download für den PDF-Wettennachweis aus.
 */
export async function downloadWettenPdf() {
  const response = await fetch(`${API_BASE_URL}/wetten/abrechnung/pdf`, {credentials: 'include'})
  if (!response.ok) {
    const text = await response.text()
    throw new Error(text || `Download fehlgeschlagen (${response.status})`)
  }
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'wetten-abrechnung.pdf'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/**
 * Ruft eine nach Guthaben sortierte Liste aller Benutzer ab.
 */
export async function getAllUsersByBalance() {
  const data = await request('/users/all')
  return data.answer
}

/**
 * Fordert einen Code für den Passwort-Reset an.
 * @param {string} email
 */
export async function requestPasswordResetCode(email) {
  return request('/reset', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/**
 * Verifiziert einen Passwort-Reset-Code.
 * @param {string} email
 * @param {string} code
 */
export async function verifyPasswordResetCode(email, code) {
  return request('/reset', {
    method: 'POST',
    body: JSON.stringify({ email, code }),
  });
}